import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Button, Alert, Spinner, Modal, Form, Badge, Row, Col } from 'react-bootstrap';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { siteConfigAPI } from '../../services/api';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  BookOpenIcon,
} from '@heroicons/react/24/solid';

interface GuideSection {
  id: number;
  title: string;
  slug: string;
  group: string;
  group_label: string;
  content: string;
  sort_order: number;
  is_published: boolean;
}

const GROUP_OPTIONS = [
  { value: 'owner', label: 'For Bakery Owners', color: '#3b82f6' },
  { value: 'customer', label: 'For Wholesale Customers', color: '#10b981' },
  { value: 'security', label: 'Account & Security', color: '#ef4444' },
];

const AdminUserGuide: React.FC = () => {
  const [sections, setSections] = useState<GuideSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [filterGroup, setFilterGroup] = useState<string>('all');

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<GuideSection | null>(null);
  const [form, setForm] = useState({ title: '', slug: '', group: 'owner', content: '', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; title: string } | null>(null);

  const loadSections = useCallback(async () => {
    try {
      const res = await siteConfigAPI.getGuideSections();
      setSections(res.data);
    } catch {
      setError('Failed to load guide sections');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSections(); }, [loadSections]);

  const slugify = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  const openModal = (section?: GuideSection) => {
    if (section) {
      setEditing(section);
      setForm({ title: section.title, slug: section.slug, group: section.group, content: section.content, sort_order: section.sort_order });
    } else {
      setEditing(null);
      setForm({ title: '', slug: '', group: 'owner', content: '', sort_order: sections.length });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.slug.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await siteConfigAPI.updateGuideSection(editing.id, form);
        setSuccess('Section updated');
      } else {
        await siteConfigAPI.createGuideSection(form);
        setSuccess('Section created');
      }
      setShowModal(false);
      loadSections();
    } catch (err: any) {
      const detail = err?.response?.data;
      if (detail?.slug) {
        setError(`Slug error: ${detail.slug[0]}`);
      } else {
        setError('Failed to save section');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePublished = async (section: GuideSection) => {
    try {
      await siteConfigAPI.updateGuideSection(section.id, { is_published: !section.is_published });
      loadSections();
    } catch {
      setError('Failed to update section');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await siteConfigAPI.deleteGuideSection(deleteTarget.id);
      setSuccess('Section deleted');
      loadSections();
    } catch {
      setError('Failed to delete section');
    } finally {
      setDeleteTarget(null);
    }
  };

  const filtered = filterGroup === 'all' ? sections : sections.filter(s => s.group === filterGroup);

  // Group sections for display
  const grouped = GROUP_OPTIONS.map(g => ({
    ...g,
    sections: filtered.filter(s => s.group === g.value),
  })).filter(g => g.sections.length > 0);

  if (loading) {
    return (
      <div className="d-flex flex-column min-vh-100 app-background">
        <Header />
        <div className="flex-grow-1 d-flex justify-content-center align-items-center">
          <Spinner animation="border" variant="primary" />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column min-vh-100 app-background">
      <Header />
      <div className="flex-grow-1">
        <Container className="py-3 py-md-5">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h1 className="display-5 fw-bold text-primary mb-1">User Guide</h1>
              <p className="text-muted mb-0">Manage sections shown on the public documentation page</p>
            </div>
            <Button variant="primary" onClick={() => openModal()} className="d-flex align-items-center gap-2">
              <PlusIcon style={{ width: 18, height: 18 }} /> New Section
            </Button>
          </div>

          {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
          {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

          {/* Filter */}
          <div className="d-flex align-items-center gap-2 mb-4">
            <Button variant={filterGroup === 'all' ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setFilterGroup('all')}>
              All ({sections.length})
            </Button>
            {GROUP_OPTIONS.map(g => {
              const count = sections.filter(s => s.group === g.value).length;
              return (
                <Button key={g.value} variant={filterGroup === g.value ? 'primary' : 'outline-secondary'} size="sm" onClick={() => setFilterGroup(g.value)}>
                  {g.label} ({count})
                </Button>
              );
            })}
          </div>

          {sections.length === 0 ? (
            <Card className="shadow-sm text-center py-5">
              <Card.Body>
                <BookOpenIcon style={{ width: 48, height: 48, color: '#cbd5e1' }} className="mb-3" />
                <h5 className="text-muted">No guide sections yet</h5>
                <p className="text-muted mb-3">Create your first section to start building the user guide.</p>
                <Button variant="primary" onClick={() => openModal()}>Create Section</Button>
              </Card.Body>
            </Card>
          ) : (
            grouped.map(group => (
              <div key={group.value} className="mb-4">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <div style={{ width: 4, height: 20, borderRadius: 2, background: group.color }} />
                  <h5 className="mb-0 fw-bold" style={{ color: group.color, fontSize: '1rem' }}>{group.label}</h5>
                </div>
                {group.sections.map(section => (
                  <Card key={section.id} className="shadow-sm mb-2">
                    <Card.Body className="py-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            <h6 className="mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>{section.title}</h6>
                            <Badge bg={section.is_published ? 'success' : 'secondary'} className="fw-normal" style={{ fontSize: '0.65rem' }}>
                              {section.is_published ? 'Published' : 'Draft'}
                            </Badge>
                          </div>
                          <div className="d-flex align-items-center gap-3 mt-1">
                            <small className="text-muted">/{section.slug}</small>
                            <small className="text-muted">Order: {section.sort_order}</small>
                            <small className="text-muted">{section.content.length} chars</small>
                          </div>
                        </div>
                        <div className="d-flex align-items-center gap-1">
                          <Button variant="link" size="sm" className="p-1" onClick={() => handleTogglePublished(section)}>
                            {section.is_published
                              ? <EyeIcon style={{ width: 16, height: 16, color: '#10b981' }} />
                              : <EyeSlashIcon style={{ width: 16, height: 16, color: '#94a3b8' }} />}
                          </Button>
                          <Button variant="link" size="sm" className="p-1" onClick={() => openModal(section)}>
                            <PencilSquareIcon style={{ width: 16, height: 16, color: '#3b82f6' }} />
                          </Button>
                          <Button variant="link" size="sm" className="p-1" onClick={() => setDeleteTarget({ id: section.id, title: section.title })}>
                            <TrashIcon style={{ width: 16, height: 16, color: '#ef4444' }} />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            ))
          )}
        </Container>
      </div>
      <Footer />

      {/* Section Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Section' : 'New Section'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  value={form.title}
                  onChange={e => {
                    const title = e.target.value;
                    setForm(p => ({
                      ...p,
                      title,
                      slug: editing ? p.slug : slugify(title),
                    }));
                  }}
                  placeholder="e.g., Creating Your Account"
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Slug</Form.Label>
                <Form.Control value={form.slug} onChange={e => setForm(p => ({ ...p, slug: e.target.value }))} placeholder="creating-account" />
                <Form.Text className="text-muted">URL anchor</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Group</Form.Label>
                <Form.Select value={form.group} onChange={e => setForm(p => ({ ...p, group: e.target.value }))}>
                  {GROUP_OPTIONS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Sort Order</Form.Label>
                <Form.Control type="number" min={0} value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>Content <small className="text-muted">(HTML supported)</small></Form.Label>
            <Form.Control
              as="textarea"
              rows={12}
              value={form.content}
              onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
              placeholder="Write the section content. HTML tags are supported for formatting."
              style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !form.title.trim() || !form.slug.trim()}>
            {saving ? <Spinner size="sm" animation="border" /> : editing ? 'Save Changes' : 'Create Section'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete section "<strong>{deleteTarget?.title}</strong>"?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminUserGuide;
